import React, { useState, useEffect } from 'react';
import '../FullInventoryForm/styles.css';
import './styles.css';
import { useGetInventoryCountRequest, useCreateInventoryRequest } from '../../../../hooks/useApi';
import FilterSelectionModal from '../FilterSelectionModal';

export const PartialInventoryForm = ({posId, onBack, onClose, onSuccess}) =>
{
  const [inventoryName, setInventoryName] = useState('');
  const [activeFilterModal, setActiveFilterModal] = useState(null); // 'season', 'line', 'gender', 'category'
  const [selectedFilters, setSelectedFilters] = useState({
    seasons: [],
    genders: [],
    lines: [],
    macroFamilies: [],
    microFamilies: []
  });
  const [count, setCount] = useState(null);

  const [{data: countData, loading: loadingCount}, getCount] = useGetInventoryCountRequest(posId);
  const [{data: createData, loading: creating, error: createError}, createInventory] = useCreateInventoryRequest(posId);

  // Update count whenever filters change
  useEffect(() =>
  {
    const filters = [];

    // Each filter value should be a separate object with type and value
    selectedFilters.seasons.forEach(season =>
    {
      filters.push({type: 'SEASON', value: season.id});
    });

    selectedFilters.genders.forEach(gender =>
    {
      filters.push({type: 'GENDER', value: gender.id});
    });

    selectedFilters.lines.forEach(line =>
    {
      filters.push({type: 'LINE', value: line.id});
    });

    selectedFilters.macroFamilies.forEach(macro =>
    {
      filters.push({type: 'MACROFAMILY', value: macro.id});
    });

    selectedFilters.microFamilies.forEach(micro =>
    {
      filters.push({type: 'MICROFAMILY', value: micro.id});
    });

    getCount({
      bodyParams: {
        inventoryType: 'PARTIAL',
        filters
      }
    });
  }, [selectedFilters]);

  useEffect(() =>
  {
    if (countData !== null && countData !== undefined)
    {
      setCount(countData);
    }
  }, [countData]);

  useEffect(() =>
  {
    if (createData && !creating && !createError)
    {
      onSuccess?.();
      onClose();
    }
  }, [createData, creating, createError]);

  const handleFilterSelect = (filterType, values, labels = {}) =>
  {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: values.map(id => ({id, label: labels[id] || id}))
    }));
    setActiveFilterModal(null);
  };

  const handleRemoveFilter = (filterType, valueId) =>
  {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].filter(item => item.id !== valueId)
    }));
  };

  const handleCreate = () =>
  {
    if (!inventoryName.trim())
    {
      alert('Please enter an inventory name');
      return;
    }

    const filters = [];
    if (selectedFilters.seasons.length > 0)
    {
      selectedFilters.seasons.forEach(season =>
      {
        filters.push({type: 'SEASON', value: season.id});
      });
    }
    if (selectedFilters.genders.length > 0)
    {
      selectedFilters.genders.forEach(gender =>
      {
        filters.push({type: 'GENDER', value: gender.id});
      });
    }
    if (selectedFilters.lines.length > 0)
    {
      selectedFilters.lines.forEach(line =>
      {
        filters.push({type: 'LINE', value: line.id});
      });
    }
    if (selectedFilters.macroFamilies.length > 0)
    {
      selectedFilters.macroFamilies.forEach(macro =>
      {
        filters.push({type: 'MACROFAMILY', value: macro.id});
      });
    }
    if (selectedFilters.microFamilies.length > 0)
    {
      selectedFilters.microFamilies.forEach(micro =>
      {
        filters.push({type: 'MICROFAMILY', value: micro.id});
      });
    }

    createInventory({
      bodyParams: {
        inventoryType: 'PARTIAL',
        name: inventoryName.trim(),
        filters
      }
    });
  };

  const getTotalSelectedCount = () =>
  {
    return (
      selectedFilters.seasons.length +
      selectedFilters.genders.length +
      selectedFilters.lines.length +
      selectedFilters.macroFamilies.length +
      selectedFilters.microFamilies.length
    );
  };

  return (
    <>
      <div className="modal-header">
        <button className="back-button" onClick={onBack}>‹</button>
        <h2 className="modal-title">Create Partial Inventory</h2>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>

      <div className="modal-body">
        <div className="form-group">
          <label className="form-label">Inventory Name</label>
          <input
            type="text"
            className="form-input"
            placeholder="INV 2"
            value={inventoryName}
            onChange={(e) => setInventoryName(e.target.value)}
          />
        </div>

        <div className="filters-section">
          <h3 className="section-title">Select Filters</h3>

          <button
            className="filter-select-button"
            onClick={() => setActiveFilterModal('season')}
          >
            <div className="filter-name">
              <span className="filter-select-label">Select Season</span>
              {selectedFilters.seasons.length > 0 && (
                <div className="filter-tags">
                  {selectedFilters.seasons.slice(0, 5).map((season, idx) => (
                    <span key={idx} className="filter-tag">
                    {season.label}
                      <span
                        className="filter-tag-remove"
                        onClick={(e) =>
                        {
                          e.stopPropagation();
                          handleRemoveFilter('seasons', season.id);
                        }}
                      >
                      ×
                    </span>
                  </span>
                  ))}
                  {selectedFilters.seasons.length > 5 && (
                    <span className="filter-tag">+{selectedFilters.seasons.length - 5}</span>
                  )}
                </div>
              )}
            </div>
            <span className="filter-select-arrow">›</span>
          </button>

          <button
            className="filter-select-button"
            onClick={() => setActiveFilterModal('gender')}
          >
            <div className="filter-name">
              <span className="filter-select-label">Select Gender</span>
              {selectedFilters.genders.length === 0 ? (
                <span className="filter-select-placeholder">No filters selected</span>
              ) : (
                <div className="filter-tags">
                  {selectedFilters.genders.slice(0, 5).map((gender, idx) => (
                    <span key={idx} className="filter-tag">
                    {gender.label}
                      <span
                        className="filter-tag-remove"
                        onClick={(e) =>
                        {
                          e.stopPropagation();
                          handleRemoveFilter('genders', gender.id);
                        }}
                      >
                      ×
                    </span>
                  </span>
                  ))}
                  {selectedFilters.genders.length > 5 && (
                    <span className="filter-tag">+{selectedFilters.genders.length - 5}</span>
                  )}
                </div>
              )}
            </div>
            <span className="filter-select-arrow">›</span>
          </button>

          <button
            className="filter-select-button"
            onClick={() => setActiveFilterModal('line')}
          >
            <div className="filter-name">
              <span className="filter-select-label">Select Line</span>
              {selectedFilters.lines.length === 0 ? (
                <span className="filter-select-placeholder">No filters selected</span>
              ) : (
                <div className="filter-tags">
                  {selectedFilters.lines.slice(0, 5).map((line, idx) => (
                    <span key={idx} className="filter-tag">
                    {line.label}
                      <span
                        className="filter-tag-remove"
                        onClick={(e) =>
                        {
                          e.stopPropagation();
                          handleRemoveFilter('lines', line.id);
                        }}
                      >
                      ×
                    </span>
                  </span>
                  ))}
                  {selectedFilters.lines.length > 5 && (
                    <span className="filter-tag">+{selectedFilters.lines.length - 5}</span>
                  )}
                </div>
              )}
            </div>
            <span className="filter-select-arrow">›</span>
          </button>

          <button
            className="filter-select-button"
            onClick={() => setActiveFilterModal('macroFamily')}
          >
            <div className="filter-name">
              <span className="filter-select-label">Select Macro Family</span>
              {selectedFilters.macroFamilies.length === 0 ? (
                <span className="filter-select-placeholder">No filters selected</span>
              ) : (
                <div className="filter-tags">
                  {selectedFilters.macroFamilies.slice(0, 5).map((macro, idx) => (
                    <span key={idx} className="filter-tag">
                    {macro.label}
                      <span
                        className="filter-tag-remove"
                        onClick={(e) =>
                        {
                          e.stopPropagation();
                          handleRemoveFilter('macroFamilies', macro.id);
                        }}
                      >
                      ×
                    </span>
                  </span>
                  ))}
                  {selectedFilters.macroFamilies.length > 5 && (
                    <span className="filter-tag">+{selectedFilters.macroFamilies.length - 5}</span>
                  )}
                </div>
              )}
            </div>
            <span className="filter-select-arrow">›</span>
          </button>

          <button
            className="filter-select-button"
            onClick={() => setActiveFilterModal('microFamily')}
          >
            <div className="filter-name">
              <span className="filter-select-label">Select Micro Family</span>
              {selectedFilters.microFamilies.length === 0 ? (
                <span className="filter-select-placeholder">No filters selected</span>
              ) : (
                <div className="filter-tags">
                  {selectedFilters.microFamilies.slice(0, 5).map((micro, idx) => (
                    <span key={idx} className="filter-tag">
                    {micro.label}
                      <span
                        className="filter-tag-remove"
                        onClick={(e) =>
                        {
                          e.stopPropagation();
                          handleRemoveFilter('microFamilies', micro.id);
                        }}
                      >
                      ×
                    </span>
                  </span>
                  ))}
                  {selectedFilters.microFamilies.length > 5 && (
                    <span className="filter-tag">+{selectedFilters.microFamilies.length - 5}</span>
                  )}
                </div>
              )}
            </div>
            <span className="filter-select-arrow">›</span>
          </button>
        </div>

        {count !== null && (
          <div className="count-display">
            <span className="count-label">Inventory Count:</span>
            <span className="count-value">{loadingCount ? '...' : `${count} pcs`}</span>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={creating || !inventoryName.trim()}
          >
            {creating ? 'Creating...' : 'Create Inventory'}
          </button>
        </div>
      </div>

      {activeFilterModal && (
        <FilterSelectionModal
          posId={posId}
          filterType={activeFilterModal}
          selectedValues={
            activeFilterModal === 'season'
              ? selectedFilters.seasons.map(s => s.id)
              : activeFilterModal === 'gender'
                ? selectedFilters.genders.map(g => g.id)
                : activeFilterModal === 'line'
                  ? selectedFilters.lines.map(l => l.id)
                  : activeFilterModal === 'macroFamily'
                    ? selectedFilters.macroFamilies.map(m => m.id)
                    : selectedFilters.microFamilies.map(m => m.id)
          }
          selectedFilters={{
            seasons: selectedFilters.seasons.map(s => s.id),
            genders: selectedFilters.genders.map(g => g.id),
            lines: selectedFilters.lines.map(l => l.id),
            macroFamilies: selectedFilters.macroFamilies.map(m => m.id),
            microFamilies: selectedFilters.microFamilies.map(m => m.id)
          }}
          onClose={() => setActiveFilterModal(null)}
          onSelect={handleFilterSelect}
        />
      )}
    </>
  );
};

export default PartialInventoryForm;
